import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, FolderTree, MapPin, ShoppingCart,
  Calendar, Users, Settings, FileText, LogOut, Menu, X, ChevronDown,
  Bell, Search, Wrench, Palette, Globe, Shield, BookOpen, Cog, Home,
  BarChart3, MessageSquare, Star, Droplets, Receipt
} from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, adminUser, loading, logout } = useAdminAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await adminFetch(`${BACKEND_URL}/api/admin/stats`);
        if (res.ok) {
          setStats(await res.json());
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/homepage-showcase', icon: Home, label: 'Homepage Showcase' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Orders', badge: 'pending_orders' },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings' },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/categories', icon: FolderTree, label: 'Categories' },
    { path: '/admin/services', icon: Wrench, label: 'Services' },
    { path: '/admin/spare-parts', icon: Cog, label: 'Spare Parts' },
    { path: '/admin/rent-models', icon: Droplets, label: 'RO on Rent Models' },
    { path: '/admin/blogs', icon: BookOpen, label: 'Blog Posts' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/team', icon: Users, label: 'Team' },
    { path: '/admin/pincodes', icon: MapPin, label: 'Pincodes' },
    { path: '/admin/cms', icon: Palette, label: 'Page Editor' },
    { path: '/admin/city-pages', icon: MapPin, label: 'City Landing Pages' },
    { path: '/admin/whatsapp-templates', icon: MessageSquare, label: 'Notification Templates' },
    { path: '/admin/notifications', icon: Bell, label: 'Notifications & SEO' },
    { path: '/admin/settings', icon: Settings, label: 'Site Settings' },
    { path: '/admin/business-settings', icon: Receipt, label: 'GST / Invoice Settings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // Don't render layout if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 bg-gray-800 flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold">RD</span>
            </div>
            <span className="text-white font-semibold">RO Dekho Admin</span>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 mt-6 px-3 overflow-y-auto admin-sidebar-scroll" data-testid="admin-sidebar-nav">
          <div className="space-y-1 pb-4">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
                {item.label === 'Orders' && stats?.pending_orders > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.pending_orders}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* Bottom — fixed footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-800 space-y-2 bg-gray-900">
          <Link 
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Globe size={20} />
            <span>Back to Website</span>
          </Link>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-600 hover:text-gray-900"
              >
                <Menu size={24} />
              </button>
              <div className="hidden md:flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                <Search size={18} className="text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search..." 
                  className="bg-transparent border-none outline-none w-64"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:text-gray-900">
                <Bell size={20} />
                {stats?.pending_orders > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              {/* User Menu */}
              <div className="relative">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">
                    {adminUser?.name || 'Admin'}
                  </span>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>

                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border z-50">
                      <div className="px-4 py-3 border-b">
                        <p className="text-sm font-medium text-gray-900">{adminUser?.name || 'Admin'}</p>
                        <p className="text-xs text-gray-500">{adminUser?.role || 'Administrator'}</p>
                      </div>
                      <div className="py-2">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut size={16} />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet context={{ stats }} />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default AdminLayout;
